<template>
  <div>
    <h4 class="employee-import-stats__header"><i :class="classes" />{{ importHeader }}</h4>
    <button class="btn btn-primary mt-2 mb-3" @click="$emit('create')" v-if="isFailed || isComplete || isFirstImport">
      Start New Import
    </button>
    <div v-if="hasStats">
      <div class="stats-row">
        <div class="stats-row__label">Created:</div>
        <div class="stats-row__value">{{ toReadableDate(stats.created_at) }}</div>
      </div>
      <div class="stats-row">
        <div class="stats-row__label">Updated:</div>
        <div class="stats-row__value">{{ toReadableDate(stats.updated_at) }}</div>
      </div>
      <div class="stats-row">
        <div class="stats-row__label">Create Infinity Booking:</div>
        <div class="stats-row__value">{{ toReadableBoolean(stats.create_infinity_event_forced) }}</div>
      </div>
      <div class="stats-row">
        <div class="stats-row__label">Delete Not Listed Employees:</div>
        <div class="stats-row__value">{{ toReadableBoolean(stats.delete_not_listed_employees) }}</div>
      </div>
      <div class="stats-row">
        <div class="stats-row__label">Delete Not Listed Users:</div>
        <div class="stats-row__value">{{ toReadableBoolean(stats.delete_not_listed_users) }}</div>
      </div>
      <div class="stats-row">
        <div class="stats-row__label">Create Users:</div>
        <div class="stats-row__value">{{ toReadableBoolean(stats.create_users) }}</div>
      </div>
      <div class="stats-row" v-if="bookingEnabled">
        <div class="stats-row__label">Release Desks:</div>
        <div class="stats-row__value">{{ toReadableBoolean(stats.release_desks) }}</div>
      </div>
      <div v-for="field in fields" :key="field" class="stats-row">
        <div class="stats-row__label">{{ getLogLabel(field) }}:</div>
        <div class="stats-row__value">
          {{ getLogValue(field) }}
        </div>
      </div>
      <div v-if="log.errors && log.errors.length" class="mt-4">
        <button v-if="errorsExpanded" class="btn btn-default mb-2" @click="errorsExpanded = false">
          Hide Errors
        </button>
        <button v-else class="btn btn-default mb-2" @click="errorsExpanded = true">
          Show Errors
        </button>

        <div>
          <ul v-if="errorsExpanded" class="stats-errors">
            <li v-for="(error, i) in log.errors" :key="i">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Api } from '@/utils/Api'
import UrlStore from '@/utils/UrlStore'
import startCase from 'lodash/startCase'
import { mapActions } from 'vuex'

const employeesImportApi = new Api(UrlStore.EMPLOYEE_IMPORTS)
const StatsReloadMs = 5000
const STATUS = {
  NEW: 0,
  DONE: 2,
  PROCESSING: 1,
  FAILED_TEMPORARY: 3,
  FAILED_PERMANENTLY: 4
}

function toReadableDate(dateString) {
  let date = new Date(dateString)
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
}
function toReadableBoolean(v) {
  return v ? 'Yes' : 'No'
}

export default {
  name: 'EmployeesImportStats',
  props: {
    bookingEnabled: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    loaded: false,
    errorsExpanded: false,
    stats: {},
    statsTimeoutId: null,
    statsTimeoutStopped: false
  }),
  computed: {
    isFirstImport() {
      return this.loaded && !this.hasStats
    },
    isWaitProcessing() {
      return this.hasStats && this.stats.status === STATUS.NEW
    },
    isInprogress() {
      return this.hasStats && [STATUS.PROCESSING, STATUS.FAILED_TEMPORARY].includes(this.stats.status)
    },
    isComplete() {
      return this.hasStats && this.stats.status === STATUS.DONE
    },
    isFailed() {
      return this.hasStats && this.stats.status === STATUS.FAILED_PERMANENTLY
    },
    hasStats() {
      return Object.keys(this.stats).length > 0
    },
    log() {
      return this.stats?.log ? JSON.parse(this.stats?.log) : {}
    },
    fields() {
      return Object.keys(this.log).filter((field) => field !== 'errors')
    },
    hasErrors() {
      return !!this.stats.log.errors.length
    },
    importHeader() {
      if (this.isWaitProcessing) return 'Employee Import Wait Processing'
      if (this.isComplete) return 'Employee Import Completed'
      if (this.isInprogress) return 'Employee Import in Progress'
      if (this.isFailed) return 'Employee Import Failed'
      return 'Import Your First Employee List'
    },
    classes() {
      return {
        ion: true,
        'ion-md-cloud-done': this.isComplete,
        'ion-md-refresh': this.isInprogress,
        'ion-md-close-circle': this.isFailed,
        'ion-md-timer': this.isWaitProcessing
      }
    }
  },
  created() {
    this.statsLoad()
  },
  beforeDestroy() {
    this.statsTimeoutStopped = true
    clearTimeout(this.statsTimeoutId)
  },
  methods: {
    ...mapActions(['loadCommonData']),
    toReadableDate,
    toReadableBoolean,
    getLogLabel(propName) {
      return startCase(propName)
    },
    getLogValue(propName) {
      return this.log[propName]
    },
    async statsLoad() {
      if (this.statsTimeoutStopped) {
        return
      }
      try {
        const r = await employeesImportApi.getAll()
        if (r.status === 200) {
          this.loaded = true
          this.$set(this, 'stats', r.data.items[0] ?? {})
        }
      } finally {
        if (this.isInprogress || this.isWaitProcessing) {
          this.statsTimeoutId = setTimeout(this.statsLoad.bind(this), StatsReloadMs)
        } else if (this.isComplete) {
          this.loadCommonData({ force: true })
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.stats-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 10px;

  &__label {
    text-align: end;
    font-weight: bolder;
  }

  &__value {
    text-align: start;
  }
}

.stats-errors {
  list-style: none;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
}

.employee-import-stats__header {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 10px;
}
</style>
